import React, { useState, useEffect } from 'react';
import { Check, ArrowRight, Star, Lock } from 'lucide-react';
import currencyService from '../../../services/currencyService';

const PlanComparisonCard = ({ plan, currentPlan, onUpgrade, selectedCurrency = 'GBP' }) => {
  const [selectedInterval, setSelectedInterval] = useState('monthly');
  const [convertedPrices, setConvertedPrices] = useState({ monthly: 0, yearly: 0 });
  const isCurrentPlan = plan?.planType === currentPlan?.planType;
  const isFreePlan = plan?.planType === 'basic';
  const isPremiumPlan = plan?.planType === 'premium';

  useEffect(() => {
    convertPrices();
  }, [selectedCurrency, plan]);

  const convertPrices = async () => {
    // Premium is £4.99/month, £49.99/year
    const monthlyGBP = isPremiumPlan ? (parseFloat(plan?.priceMonthly) || 4.99) : 0;
    const yearlyGBP = isPremiumPlan ? (parseFloat(plan?.priceYearly) || 49.99) : 0;

    if (selectedCurrency === 'GBP') {
      setConvertedPrices({ monthly: monthlyGBP, yearly: yearlyGBP });
    } else {
      const monthlyConverted = await currencyService?.convertFromGBP(monthlyGBP, selectedCurrency);
      const yearlyConverted = await currencyService?.convertFromGBP(yearlyGBP, selectedCurrency);
      setConvertedPrices({ monthly: monthlyConverted, yearly: yearlyConverted });
    }
  };

  const getCardStyle = () => {
    if (isFreePlan) return 'border-2 border-primary shadow-lg';
    if (isPremiumPlan) return 'border-2 border-gray-900 shadow-md';
    return 'border border-gray-200';
  };

  const getButtonStyle = () => {
    if (isCurrentPlan) return 'bg-gray-100 text-gray-600 cursor-not-allowed';
    if (isFreePlan) return 'bg-primary text-white hover:bg-primary/90';
    return 'bg-gray-900 text-white hover:bg-gray-800';
  };

  const freeFeatures = [
    'Unlimited symptom tracking',
    'Symptom timeline view',
  ];

  const premiumFeatures = [
    'Everything in Free Plan',
    'Doctor consultation summary',
    'Patient notes',
    'Trigger tracking',
    'Doctor appointment reminders (up to 3)',
    'Advanced analytics & AI insights',
    'Priority support',
  ];

  const premiumOnlyFeatures = [
    'Doctor consultation summary',
    'Patient notes',
    'Trigger tracking',
    'Doctor appointment reminders (up to 3)',
  ];

  const handleUpgradeClick = () => {
    if (!isCurrentPlan) {
      onUpgrade(plan?.planType, isFreePlan ? 'monthly' : selectedInterval);
    }
  };

  return (
    <div className={`bg-white rounded-lg p-6 ${getCardStyle()} transition-all relative`}>
      {isFreePlan && (
        <div className="absolute -top-4 left-1/2 -translate-x-1/2">
          <div className="bg-primary text-white text-sm font-bold px-4 py-1.5 rounded-full shadow flex items-center gap-1">
            <Star className="w-3.5 h-3.5" />
            Recommended — Start here
          </div>
        </div>
      )}
      {isPremiumPlan && (
        <div className="absolute -top-4 left-1/2 -translate-x-1/2">
          <div className="bg-gray-900 text-white text-sm font-bold px-4 py-1.5 rounded-full shadow">
            Most Popular
          </div>
        </div>
      )}

      <div className="mt-4">
        <h3 className="text-2xl font-bold text-gray-900 mb-2">
          {isFreePlan ? 'Free Forever' : 'Premium'}
        </h3>
        <p className="text-gray-600 text-sm mb-4">
          {isFreePlan
            ? 'Track your symptoms — free forever'
            : 'Prepare for consultations. Track triggers. Take notes.'}
        </p>
      </div>
      
      <div className="mb-6">
        {isFreePlan ? (
          <div>
            <div className="text-3xl font-bold text-gray-900">Free</div>
            <p className="text-sm text-gray-500 mt-1">Forever. No credit card required.</p>
          </div>
        ) : (
          <div>
            <div className="flex items-center justify-center gap-2 mb-4 bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setSelectedInterval('monthly')}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  selectedInterval === 'monthly' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setSelectedInterval('yearly')}
                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                  selectedInterval === 'yearly' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Yearly
              </button>
            </div>

            <div className="text-center">
              <div className="text-3xl font-bold text-gray-900">
                {currencyService?.formatPrice(
                  selectedInterval === 'yearly' ? convertedPrices?.yearly : convertedPrices?.monthly,
                  selectedCurrency
                )}
                <span className="text-lg text-gray-600 font-normal">/{selectedInterval === 'yearly' ? 'year' : 'month'}</span>
              </div>
              {selectedInterval === 'yearly' && convertedPrices?.monthly > 0 && (
                <div className="text-sm text-green-600 font-medium mt-1">
                  Save {Math.round((1 - (convertedPrices?.yearly / (convertedPrices?.monthly * 12))) * 100)}% annually
                </div>
              )}
              {selectedCurrency !== 'GBP' && (
                <div className="text-xs text-gray-500 mt-1">
                  Converted from £{selectedInterval === 'yearly' ? (plan?.priceYearly || '49.99') : (plan?.priceMonthly || '4.99')} GBP
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <button
        onClick={handleUpgradeClick}
        disabled={isCurrentPlan}
        className={`w-full py-3 px-4 rounded-lg font-semibold transition-colors flex items-center justify-center ${getButtonStyle()}`}
      >
        {isCurrentPlan ? (
          'Current Plan'
        ) : (
          <>
            {isFreePlan ? 'Get started free' : `Upgrade ${selectedInterval === 'yearly' ? 'Yearly' : 'Monthly'} →`}
            {!isFreePlan && <ArrowRight className="w-5 h-5 ml-2" />}
          </>
        )}
      </button>

      <div className="mt-6 space-y-3">
        <p className="font-semibold text-gray-900 text-sm">Includes:</p>
        {(isFreePlan ? freeFeatures : premiumFeatures)?.map((feature, i) => (
          <div key={i} className="flex items-start">
            <Check className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
            <span className="text-sm text-gray-700">{feature}</span>
          </div>
        ))}
        {isFreePlan && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Premium only:</p>
            {premiumOnlyFeatures?.map((feature, i) => (
              <div key={i} className="flex items-start mb-2">
                <Lock className="w-4 h-4 text-gray-300 mr-2 flex-shrink-0 mt-0.5" />
                <span className="text-sm text-gray-400">{feature}</span>
              </div>
            ))}
          </div>
        )}
        {isPremiumPlan && (
          <p className="text-xs text-gray-400 italic mt-2">Doctor appointment reminders included. Advanced analytics and AI insights coming soon.</p>
        )}
      </div>
    </div>
  );
};

export default PlanComparisonCard;